import { useState } from 'react';

export function Button({ children, onClick, variant = 'primary', size = 'md', disabled, loading, style }) {
  const styles = {
    base: {
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      fontFamily: 'var(--font-display)', fontWeight: 600, borderRadius: 'var(--radius)',
      border: 'none', transition: 'var(--transition)', cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
    },
    sizes: {
      sm: { padding: '7px 16px', fontSize: 13 },
      md: { padding: '11px 22px', fontSize: 14 },
      lg: { padding: '15px 32px', fontSize: 16 },
      xl: { padding: '18px 40px', fontSize: 18 },
    },
    variants: {
      primary: { background: 'var(--cobalt)', color: '#fff', boxShadow: '0 2px 12px rgba(21,101,192,0.3)' },
      secondary: { background: 'var(--navy-mid)', color: '#fff' },
      success: { background: 'var(--green-dark)', color: '#fff', boxShadow: '0 2px 12px rgba(0,150,36,0.25)' },
      danger: { background: 'var(--red)', color: '#fff', boxShadow: '0 2px 12px rgba(244,67,54,0.25)' },
      amber: { background: 'var(--amber)', color: 'var(--navy)' },
      ghost: { background: 'transparent', color: 'var(--cobalt)', border: '1.5px solid var(--cobalt)' },
      dark: { background: 'var(--navy)', color: '#fff' },
    }
  };
  return (
    <button
      onClick={onClick} disabled={disabled || loading}
      style={{ ...styles.base, ...styles.sizes[size], ...styles.variants[variant], ...style }}
    >
      {loading && <span style={{ width:16,height:16,border:'2px solid rgba(255,255,255,0.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin 0.7s linear infinite',display:'inline-block' }} />}
      {children}
    </button>
  );
}

export function Card({ children, style }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)',
      border: '1px solid var(--gray-100)', overflow: 'hidden', ...style
    }}>
      {children}
    </div>
  );
}

export function Input({ label, value, onChange, type = 'text', placeholder, required, style }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-700)', fontFamily: 'var(--font-display)' }}>{label}{required && <span style={{ color: 'var(--red)', marginLeft: 3 }}>*</span>}</label>}
      <input
        type={type} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} required={required}
        style={{
          padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: 14,
          border: '1.5px solid var(--gray-100)', outline: 'none', background: 'var(--gray-50)',
          transition: 'border-color 0.2s',
          ...style,
        }}
        onFocus={e => e.target.style.borderColor = 'var(--cobalt)'}
        onBlur={e => e.target.style.borderColor = 'var(--gray-100)'}
      />
    </div>
  );
}

export function Select({ label, value, onChange, options, style }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-700)', fontFamily: 'var(--font-display)' }}>{label}</label>}
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: 14, border: '1.5px solid var(--gray-100)', outline: 'none', background: 'var(--gray-50)', ...style }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export function Toggle({ checked, onChange, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
      <div onClick={() => onChange(!checked)} style={{
        width: 44, height: 24, borderRadius: 12, position: 'relative', transition: '0.3s',
        background: checked ? 'var(--cobalt)' : 'var(--gray-300)',
      }}>
        <div style={{
          position: 'absolute', top: 3, left: checked ? 23 : 3, width: 18, height: 18,
          borderRadius: '50%', background: '#fff', transition: '0.3s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)'
        }} />
      </div>
      {label && <span style={{ fontSize: 14, color: 'var(--gray-700)', fontWeight: 500 }}>{label}</span>}
    </label>
  );
}

export function StatusBadge({ status }) {
  const cls = `badge badge-${status?.toLowerCase()}`;
  const icons = { Waiting: '⏳', Open: '▶', Closed: '✓', Skipped: '⏭' };
  return <span className={cls}>{icons[status]} {status}</span>;
}

export function Modal({ open, onClose, title, children, width = 480 }) {
  if (!open) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      backdropFilter: 'blur(4px)'
    }} onClick={onClose}>
      <div className="animate-pop modal-inner" style={{
        background: '#fff', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: width,
        boxShadow: 'var(--shadow-lg)', overflow: 'hidden'
      }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, color: 'var(--navy)' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--gray-500)', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

export function Toast({ message, type = 'success', onClose }) {
  const colors = { success: 'var(--green-dark)', error: 'var(--red)', info: 'var(--cobalt)' };
  return (
    <div className="animate-fade" style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 2000,
      background: colors[type], color: '#fff', borderRadius: 'var(--radius)',
      padding: '12px 20px', fontSize: 14, fontWeight: 500,
      boxShadow: 'var(--shadow-lg)', display: 'flex', alignItems: 'center', gap: 10,
      maxWidth: 360,
    }}>
      {message}
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 16, marginLeft: 8 }}>×</button>
    </div>
  );
}

export function Spinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48 }}>
      <div style={{ width: 36, height: 36, border: '3px solid var(--ice-dim)', borderTopColor: 'var(--cobalt)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );
}

export function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = (message, type = 'success') => {
    const id = Date.now();
    setToasts(t => [...t, { id, message, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  };
  const ToastContainer = () => (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 2000, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {toasts.map(t => <Toast key={t.id} message={t.message} type={t.type} onClose={() => setToasts(ts => ts.filter(x => x.id !== t.id))} />)}
    </div>
  );
  return { show, ToastContainer };
}
